import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography,
} from '@mui/material';
import { Close, Description, Download, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { renderAsync } from 'docx-preview';
import { fetchAuthedBlobUrl, toProtectedFileUrl } from '../utils/secureFile';

function classify(url = '') {
  const path = String(url).split('?')[0].toLowerCase();
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(path)) return 'image';
  if (path.includes('/files/images/') || path.includes('/images/')) return 'image';
  if (/\.pdf$/i.test(path) || path.includes('/rab/file/')) return 'pdf';
  if (/\.(docx?|doc)$/i.test(path)) return 'doc';
  if (path.includes('/ba/')) return 'doc';
  return 'other';
}

function guessFileName(url = '', label = 'file') {
  try {
    const clean = String(url).split('?')[0];
    const last = clean.split('/').pop();
    if (last && last.includes('.')) return decodeURIComponent(last);
  } catch { /* ignore */ }
  return label || 'file';
}

export default function FileViewerHost() {
  const [open, setOpen] = useState(false);
  const [urls, setUrls] = useState([]);
  const [labels, setLabels] = useState([]);
  const [title, setTitle] = useState('Preview File');
  const [idx, setIdx] = useState(0);
  const [blobUrl, setBlobUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [docxOk, setDocxOk] = useState(false);
  const [docxLoading, setDocxLoading] = useState(false);
  const docxContainerRef = useRef(null);

  useEffect(() => {
    const handler = (event) => {
      const detail = event.detail || {};
      const nextUrls = (detail.urls || [])
        .filter(Boolean)
        .map((u) => toProtectedFileUrl(u));
      if (!nextUrls.length) return;
      const safeIndex = Number.isInteger(detail.activeIndex) && detail.activeIndex >= 0 && detail.activeIndex < nextUrls.length
        ? detail.activeIndex
        : 0;
      setUrls(nextUrls);
      setLabels(detail.labels || nextUrls.map((_, i) => `File ${i + 1}`));
      setTitle(detail.title || 'Preview File');
      setIdx(safeIndex);
      setOpen(true);
    };
    window.addEventListener('in-app-file-preview', handler);
    return () => window.removeEventListener('in-app-file-preview', handler);
  }, []);

  const currentUrl = urls[idx] || '';
  const currentLabel = labels[idx] || `File ${idx + 1}`;
  const kind = classify(currentUrl);

  useEffect(() => {
    let active = true;
    setBlobUrl('');
    setError('');
    setDocxOk(false);
    if (!open || !currentUrl) return undefined;

    // external non-API: no auth fetch
    if (/^https?:\/\//i.test(currentUrl) && !currentUrl.includes(process.env.REACT_APP_URL || 'localhost:5100')) {
      setBlobUrl(currentUrl);
      return undefined;
    }

    setLoading(true);
    fetchAuthedBlobUrl(currentUrl)
      .then((url) => {
        if (!active) return;
        setBlobUrl(url);
      })
      .catch((err) => {
        if (!active) return;
        setError(err?.response?.data?.message || err.message || 'Gagal memuat file (login mungkin habis)');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [open, currentUrl]);

  // Render DOCX (Berita Acara, dll) in-app menggunakan docx-preview
  useEffect(() => {
    let active = true;
    if (!open || !blobUrl || kind !== 'doc') return undefined;
    // .doc lama tidak didukung docx-preview, hanya .docx (BA sistem selalu .docx)
    const isLegacyDoc = /\.doc$/i.test(String(currentUrl).split('?')[0]) && !/\.docx$/i.test(String(currentUrl).split('?')[0]);
    if (isLegacyDoc) return undefined;

    setDocxLoading(true);
    setDocxOk(false);
    fetch(blobUrl)
      .then((r) => r.blob())
      .then(async (blob) => {
        if (!active || !docxContainerRef.current) return;
        docxContainerRef.current.innerHTML = '';
        await renderAsync(blob, docxContainerRef.current, undefined, {
          inWrapper: true,
          ignoreWidth: false,
          ignoreHeight: true,
          breakPages: true,
          useBase64URL: true,
        });
        if (active) setDocxOk(true);
      })
      .catch(() => {
        if (active) setDocxOk(false);
      })
      .finally(() => {
        if (active) setDocxLoading(false);
      });

    return () => { active = false; };
  }, [open, blobUrl, kind, currentUrl]);

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = guessFileName(currentUrl, currentLabel);
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const close = () => {
    setOpen(false);
    setUrls([]);
    setBlobUrl('');
    setError('');
    setDocxOk(false);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
        <Typography variant="subtitle1" noWrap sx={{ maxWidth: '80%' }}>
          {title} — {currentLabel}
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          {!loading && !error && blobUrl && (
            <IconButton onClick={handleDownload} aria-label="Unduh file" title="Unduh file">
              <Download />
            </IconButton>
          )}
          <IconButton onClick={close} aria-label="Tutup preview"><Close /></IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ py: 8, textAlign: 'center' }}>
            <CircularProgress />
            <Typography variant="body2" sx={{ mt: 2 }}>Memuat file terproteksi...</Typography>
          </Box>
        )}

        {!loading && error && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && blobUrl && kind === 'image' && (
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={blobUrl}
              alt={currentLabel}
              style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
            />
          </Box>
        )}

        {!loading && !error && blobUrl && kind === 'pdf' && (
          <Box sx={{ width: '100%', height: { xs: 420, md: 560 } }}>
            <object data={blobUrl} type="application/pdf" width="100%" height="100%" style={{ border: 'none' }}>
              <iframe title={currentLabel} src={blobUrl} width="100%" height="100%" style={{ border: 'none' }} />
            </object>
          </Box>
        )}

        {!loading && !error && blobUrl && kind === 'doc' && (
          <Box>
            {docxLoading && (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <CircularProgress size={28} />
                <Typography variant="body2" sx={{ mt: 1 }}>Menyiapkan preview dokumen...</Typography>
              </Box>
            )}
            {/* Container render docx-preview */}
            <Box
              ref={docxContainerRef}
              sx={{
                display: docxOk ? 'block' : 'none',
                maxHeight: '65vh',
                overflow: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                bgcolor: '#f5f5f5',
                '& .docx-wrapper': { background: '#f5f5f5', padding: '12px' },
                '& .docx-wrapper section.docx': { boxShadow: '0 0 6px rgba(0,0,0,0.2)', marginBottom: '12px' },
              }}
            />
            {!docxLoading && !docxOk && (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Description sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="body1" sx={{ mb: 1 }}>{currentLabel}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Preview dokumen ini tidak tersedia di browser. Silakan unduh untuk membuka file.
                </Typography>
                <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>
                  Unduh Dokumen
                </Button>
              </Box>
            )}
            {docxOk && (
              <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
                <Button size="small" variant="outlined" startIcon={<Download />} onClick={handleDownload}>
                  Unduh Dokumen Asli (.docx)
                </Button>
              </Stack>
            )}
          </Box>
        )}

        {!loading && !error && kind === 'other' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Description sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>{currentLabel}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Preview dokumen ini terbatas di browser. File sudah dilindungi session login.
            </Typography>
            {blobUrl && (
              <Button variant="contained" startIcon={<Download />} onClick={handleDownload}>
                Unduh Dokumen
              </Button>
            )}
          </Box>
        )}

        {urls.length > 1 && (
          <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
            <Button size="small" disabled={idx === 0} startIcon={<NavigateBefore />} onClick={() => setIdx((v) => Math.max(0, v - 1))}>
              Prev
            </Button>
            {urls.map((_, i) => (
              <Chip
                key={i}
                size="small"
                label={labels[i] || `#${i + 1}`}
                color={i === idx ? 'primary' : 'default'}
                variant={i === idx ? 'filled' : 'outlined'}
                onClick={() => setIdx(i)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            <Button size="small" disabled={idx >= urls.length - 1} endIcon={<NavigateNext />} onClick={() => setIdx((v) => Math.min(urls.length - 1, v + 1))}>
              Next
            </Button>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}
